/**
 * Calendar navigation, committed and suggested blocks, solve progress, and multi-session immersion
 * presentation state. Legacy single-session fields are derived from active sessions for
 * compatibility.
 *
 * @module apps/web/src/stores/scheduleStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addDays, startOfWeek } from 'date-fns';
import type { ScheduleBlock, TaskStateProfile } from '@fluxability/types';

/**
 * Planner view names understood by navigation and the persisted view preference.
 */
export type ViewType = 'day' | 'week' | 'month' | 'kanban' | 'table';

// Google Calendar event from API
/**
 * Normalized timed calendar commitment used by the React calendar, including protected-hold flags
 * and optional planning metadata.
 */
export interface CalendarEvent {
  /** Stable commitment/provider-event ID used by calendar blocks. */
  id: string;
  /** Display title after provider field normalization. */
  title: string;
  /** Timed commitment start supplied by the API. */
  start: string;
  /** Timed commitment end supplied by the API. */
  end: string;
  /** Whether the normalized commitment should resist ordinary activity movement. */
  isImmovable?: boolean;
  /** Whether this commitment is a protected Fluxability hold. */
  isFlexHold?: boolean;
  /** Optional planning impact; null preserves unknown scores. */
  stateProfile?: TaskStateProfile | null;
  /** Provider and planning metadata retained during normalization. */
  customAttributes?: Record<string, unknown>;
  /** Whether activities may be planned over this commitment. Defaults to false. */
  allowConflict?: boolean;
  /** Whether a series-wide rule allows conflicts for this recurring event. Defaults to false. */
  seriesAllowConflict?: boolean;
  /** Whether the commitment belongs to a recurring series. Defaults to false. */
  isRecurring?: boolean;
}

/**
 * Server solve-job progress envelope. Job identity, terminal state, counts, and the progress
 * message drive realtime completion and progress displays.
 */
export interface SolveStatus {
  /** Server solve identifier used to avoid mixing concurrent or superseded jobs. */
  job_id: string;
  /** Server lifecycle state; terminal values are recognized by terminalSolveStatus. */
  status: string;
  /** Number of activities included in the solve. */
  total: number;
  /** Activities placed by the solve so far. */
  scheduled: number;
  /** Previously scheduled blocks re-optimized so far. */
  optimized: number;
  /** Activities the solve could not place. */
  failed: number;
  /** Activities still awaiting placement. */
  pending: number;
  /** Server-supplied progress or outcome description. */
  message: string;
}

/** Browser calendar and solve presentation state, including tentative blocks, provider commitments, active immersion panels, and display preferences. Store setters do not establish canonical persistence or permission. */
export interface ScheduleState {
  /** Current server job progress, or null when no job is displayed. */
  solveStatus: SolveStatus | null;
  /** Replace the displayed job status without starting or cancelling server work. */
  setSolveStatus: (status: SolveStatus | null) => void;
  /** Date anchoring the visible calendar interval. */
  currentDate: Date;
  /** Date selected for navigation and creation controls. */
  selectedDate: Date;
  /** Selected planner representation; persisted across reloads. */
  view: ViewType;
  /** Controlled visibility of the global command dialog. */
  isCommandPaletteOpen: boolean;
  /** Legacy flag derived from the active immersion ID collection. */
  isImmersionModeActive: boolean;
  /** Legacy single-session projection of the active session collection. */
  immersionTaskId: string | null;
  /** Activities whose session panels are active, including minimized panels. */
  activeImmersionTaskIds: string[];
  /** Active panels hidden into the session tray; minimizing does not stop work. */
  minimizedImmersionTaskIds: string[];
  /** Calendar display blocks combining proposed and recorded intervals. */
  blocks: ScheduleBlock[];
  /** Normalized provider commitments visible to the current browser. */
  calendarEvents: CalendarEvent[];

  // Ghost blocks (AI suggestions)
  /** Tentative scheduling proposals with their cost/confidence explanation. */
  ghostBlocks: Array<{
    /** Tentative block ID used for confirmation. */
    id: string;
    /** Activity proposed for this tentative interval. */
    taskId: string;
    /** Suggested start instant; not yet a confirmed placement. */
    proposedStart: string;
    /** Suggested end instant; not yet a confirmed placement. */
    proposedEnd: string;
    /** Scheduler cost attached to this suggestion. */
    cost: number;
    /** Reported confidence associated with the suggestion. */
    confidence: number;
    /** Human-facing explanation of the proposed placement. */
    reason: string;
  }>;

  // Actions
  /** Set the visible interval anchor. */
  setCurrentDate: (date: Date) => void;
  /** Set the selected date without changing canonical activity data. */
  setSelectedDate: (date: Date) => void;
  /** Change the persisted planner representation. */
  setView: (view: ViewType) => void;
  /** Set both visible and selected dates to now. */
  goToToday: () => void;
  /** Move by one day, week, or month according to the current view. */
  goToPrevious: () => void;
  /** Move forward by the current view’s interval. */
  goToNext: () => void;
  /** Toggle command-dialog visibility. */
  toggleCommandPalette: () => void;
  /** Set command-dialog visibility explicitly. */
  setCommandPaletteOpen: (open: boolean) => void;
  /** Add an activity panel to the active set and synchronize legacy session fields. */
  startImmersionMode: (taskId: string) => void;
  /** Remove one requested panel, or all panels when no ID is supplied; this does not cancel server sessions. */
  endImmersionMode: (taskId?: string) => void;
  /** Hide an active panel while retaining its active-session membership. */
  minimizeImmersionMode: (taskId: string) => void;
  /** Remove a panel from the minimized collection so it becomes visible. */
  restoreImmersionMode: (taskId: string) => void;
  /** Replace the displayed suggestion collection. */
  setGhostBlocks: (blocks: ScheduleState['ghostBlocks']) => void;
  /** Clear local suggestions without making a server scheduling decision. */
  clearGhostBlocks: () => void;
  /** Replace the local timeline-block projection. */
  setBlocks: (blocks: ScheduleBlock[]) => void;
  /** Append one local display block. */
  addBlock: (block: ScheduleBlock) => void;
  /** Merge fields into the matching local display block. */
  updateBlock: (id: string, updates: Partial<ScheduleBlock>) => void;
  /** Remove one display block by ID without deleting server data. */
  deleteBlock: (id: string) => void;
  /** Replace normalized commitment records in memory. */
  setCalendarEvents: (events: CalendarEvent[]) => void;

  // Computed
  /** Produce the seven dates for the current Sunday-based store week; individual calendar views may derive a separate Monday-based range. */
  getWeekDates: () => Date[];
  /** Produce the visible month-grid date sequence for the current anchor. */
  getMonthDates: () => Date[];
}

/**
 * Zustand planner presentation store. Calendar navigation uses view-specific increments; immersion
 * actions keep active and minimized session IDs consistent with legacy single-session fields.
 * Persists only the selected view and immersion IDs under the historical key, not blocks or solve
 * progress.
 */
/**
 * Return `previous` when `next` serializes identically, otherwise `next`. A refresh that brings
 * back the same placements then keeps their identity, so calendar screens need not re-render.
 */
function keepIfEqual<T>(previous: T, next: T): T {
  return previous === next || JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
}

/** Shared calendar presentation, solve progress and schedule results for the current browser. */
export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      solveStatus: null,
      setSolveStatus: solveStatus => set({ solveStatus }),
      currentDate: new Date(),
      selectedDate: new Date(),
      view: 'week',
      isCommandPaletteOpen: false,
      isImmersionModeActive: false,
      immersionTaskId: null,
      activeImmersionTaskIds: [],
      minimizedImmersionTaskIds: [],
      ghostBlocks: [],
      blocks: [],
      calendarEvents: [],

      setCurrentDate: date => set({ currentDate: date }),
      setSelectedDate: date => set({ selectedDate: date }),
      setView: view => set({ view }),

      goToToday: () => {
        const today = new Date();
        set({ currentDate: today, selectedDate: today });
      },

      goToPrevious: () => {
        const { view, currentDate } = get();
        let newDate: Date;

        switch (view) {
          case 'day':
            newDate = addDays(currentDate, -1);
            break;
          case 'week':
            newDate = addDays(currentDate, -7);
            break;
          case 'month':
            newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            break;
          default:
            newDate = addDays(currentDate, -7);
        }

        set({ currentDate: newDate });
      },

      goToNext: () => {
        const { view, currentDate } = get();
        let newDate: Date;

        switch (view) {
          case 'day':
            newDate = addDays(currentDate, 1);
            break;
          case 'week':
            newDate = addDays(currentDate, 7);
            break;
          case 'month':
            newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
            break;
          default:
            newDate = addDays(currentDate, 7);
        }

        set({ currentDate: newDate });
      },

      toggleCommandPalette: () => {
        set(state => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen }));
      },

      setCommandPaletteOpen: open => set({ isCommandPaletteOpen: open }),

      startImmersionMode: taskId => {
        set(state => {
          const activeImmersionTaskIds = state.activeImmersionTaskIds.includes(taskId)
            ? state.activeImmersionTaskIds
            : [...state.activeImmersionTaskIds, taskId];
          return {
            activeImmersionTaskIds,
            minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.filter(id => id !== taskId),
            isImmersionModeActive: activeImmersionTaskIds.length > 0,
            immersionTaskId: activeImmersionTaskIds[0] ?? null,
          };
        });
      },

      endImmersionMode: taskId => {
        set(state => {
          const activeImmersionTaskIds = taskId
            ? state.activeImmersionTaskIds.filter(id => id !== taskId)
            : [];
          return {
            activeImmersionTaskIds,
            minimizedImmersionTaskIds: taskId
              ? state.minimizedImmersionTaskIds.filter(id => id !== taskId)
              : [],
            isImmersionModeActive: activeImmersionTaskIds.length > 0,
            immersionTaskId: activeImmersionTaskIds[0] ?? null,
          };
        });
      },

      minimizeImmersionMode: taskId => {
        set(state => ({
          minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.includes(taskId)
            ? state.minimizedImmersionTaskIds
            : [...state.minimizedImmersionTaskIds, taskId],
        }));
      },

      restoreImmersionMode: taskId => {
        set(state => ({
          activeImmersionTaskIds: state.activeImmersionTaskIds.includes(taskId)
            ? state.activeImmersionTaskIds
            : [...state.activeImmersionTaskIds, taskId],
          minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.filter(id => id !== taskId),
          isImmersionModeActive: true,
          immersionTaskId: state.immersionTaskId || taskId,
        }));
      },

      setGhostBlocks: blocks =>
        set(state => ({ ghostBlocks: keepIfEqual(state.ghostBlocks, blocks) })),
      clearGhostBlocks: () => set({ ghostBlocks: [] }),

      setBlocks: blocks => set(state => ({ blocks: keepIfEqual(state.blocks, blocks) })),

      addBlock: block => {
        set(state => ({ blocks: [...state.blocks, block] }));
      },

      updateBlock: (id, updates) => {
        set(state => ({
          blocks: state.blocks.map(block => (block.id === id ? { ...block, ...updates } : block)),
        }));
      },

      deleteBlock: id => {
        set(state => ({
          blocks: state.blocks.filter(block => block.id !== id),
        }));
      },

      setCalendarEvents: events =>
        set(state => ({ calendarEvents: keepIfEqual(state.calendarEvents, events) })),

      getWeekDates: () => {
        const { currentDate } = get();
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      },

      getMonthDates: () => {
        const { currentDate } = get();
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Get the start of the week containing the first day
        const startDate = startOfWeek(firstDay, { weekStartsOn: 0 });
        // Calculate number of weeks to show
        const weeks = Math.ceil((lastDay.getDate() + startDate.getDay()) / 7);

        const dates: Date[] = [];
        let current = startDate;

        for (let i = 0; i < weeks * 7; i++) {
          dates.push(new Date(current));
          current = addDays(current, 1);
        }

        return dates;
      },
    }),
    {
      name: 'flexiplanner-schedule',
      partialize: state => ({
        view: state.view,
        activeImmersionTaskIds: state.activeImmersionTaskIds,
        minimizedImmersionTaskIds: state.minimizedImmersionTaskIds,
      }),
    }
  )
);
